import React, { useState, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { Box, Divider } from "@mui/material";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleContainer from "../utils/ScheduleContainer";
import ScheduleNotes from "../ScheduleNotes";
import SectionBox from "../utils/SectionBox";
import FtcSettings from "./Settings";
import ForeignIncomeTable from "./ForeignIncomeTable";
import { getCommaOut } from "../../../utils/hooks/dataHandler";
import { convertCurrency } from "../../../utils/static/currency";
import FtcForm from "./FtcForm";
import PdfGenerator from "../ForeignIncomeTracker/Pdf/FtcPdf";
import { getCountry } from "../ForeignIncomeTracker/calculations";

const FtcSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState(`Foreign Tax Credit`);
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [functionalCurrency, setFunctionalCurrency] = useState("SGD");
  const [exchangeRate, setExchangeRate] = useState(1);

  const [foreignIncomeRows, setForeignIncomeRows] = useState([]);
  const [breakdownRows, setBreakdownRows] = useState({
    totalRevenue: 0,
    adjustedProfitLoss: 0,
    statutoryIncome: 0,
    taxPayable: 0,
  });
  const [ftcCalculatedData, setFtcCalculatedData] = useState({});

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const calculateForeignIncome = (rows, currency, exchangeRate) => {
    return rows.map((row) => {
      if (row.isPopulated) row.country = getCountry(row.country);
      row.netIncome = row.grossIncome - row.allowableExpenses;

      if (currency !== "SGD") {
        row.foreignTaxSufferedSGD = convertCurrency(
          row.foreignTaxSuffered,
          currency,
          "SGD",
          exchangeRate
        );
      } else {
        row.foreignTaxSufferedSGD = row.foreignTaxSuffered;
      }
      return row;
    });
  };

  const handleBreakdownChange = (value, { field }) => {
    setBreakdownRows((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const recordFtcCalculatedData = (data) => {
    setFtcCalculatedData(data);
  };

  // Initialize with sample data
  useEffect(() => {
    if (!schedule || !scheduleDetails) return;

    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      let dataRows = scheduleDetails.dataRows;

      if (dataRows) {
        dataRows = calculateForeignIncome(
          dataRows,
          company.currency,
          company.currencyRate
        );
      }
      setForeignIncomeRows(dataRows);
      setBreakdownRows(scheduleDetails.breakdownRows);
    }

    if (company) {
      setFunctionalCurrency(company.currency);
      setExchangeRate(company.currencyRate);
    }
  }, [schedule, scheduleDetails]);

  const handleSave = () => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      dataRows: foreignIncomeRows,
      breakdownRows: breakdownRows,
      totalAdjustment: ftcCalculatedData.totalFTC || 0,
      doc: JSON.stringify({
        taxAdjustment: schedule?.taxAdjustment || "",
        functionalCurrency,
        functionalCurrencyRate: exchangeRate,
        breakdownRows,
        foreignIncomeRows,
        foreignIncomeTotals,
        ftcCalculatedData,
        notes,
        reviewNotes,
      }),
    });
  };

  const handleSaveSettings = () => {
    setSettingsDialogOpen(false);
  };

  const addForeignIncomeRow = () => {
    const newRow = {
      rowId: uuidv4(),
      reference: "",
      incomeType: "",
      grossIncome: 0,
      allowableExpenses: 0,
      country: "",
      foreignTaxSuffered: 0,
      foreignTaxSufferedSGD: 0,
      isPopulated: false,
      isLocked: false,
    };
    setForeignIncomeRows([...foreignIncomeRows, newRow]);
  };

  const handleRemoveForeignIncomeRow = (rowId) => {
    const updatedRows = foreignIncomeRows?.filter((row) => row.rowId !== rowId);
    setForeignIncomeRows(updatedRows);
  };

  const handleLockForeignIncomeRow = (rowId) => {
    const updatedRows = foreignIncomeRows.map((row) =>
      row.rowId === rowId ? { ...row, isLocked: true } : row
    );
    setForeignIncomeRows(updatedRows);
  };

  const handleUnlockForeignIncomeRow = (rowId) => {
    const updatedRows = foreignIncomeRows.map((row) =>
      row.rowId === rowId ? { ...row, isLocked: false } : row
    );
    setForeignIncomeRows(updatedRows);
  };

  const handleForeignIncomeChange = (value, reference) => {
    const { rowId, field, fieldType } = reference;
    let updatedRows = foreignIncomeRows.map((row) =>
      row.rowId === rowId
        ? {
            ...row,
            [field]: fieldType === "numeric" ? getCommaOut(value) : value,
          }
        : row
    );

    if (fieldType === "numeric") {
      updatedRows = calculateForeignIncome(
        updatedRows,
        functionalCurrency,
        exchangeRate
      );
    }
    setForeignIncomeRows(updatedRows);
  };

  // Calculate totals for Foreign Income table (recompute only when foreignIncomeRows changes)
  const foreignIncomeTotals = useMemo(() => {
    return foreignIncomeRows?.reduce(
      (totals, row) => {
        return {
          grossIncome: totals.grossIncome + (row.grossIncome || 0),
          allowableExpenses:
            totals.allowableExpenses + (row.allowableExpenses || 0),
          netIncome: totals.netIncome + (row.netIncome || 0),
          foreignTaxSuffered:
            totals.foreignTaxSuffered + (Number(row.foreignTaxSuffered) || 0),
          foreignTaxSufferedSGD:
            totals.foreignTaxSufferedSGD + (row.foreignTaxSufferedSGD || 0),
        };
      },
      {
        grossIncome: 0,
        allowableExpenses: 0,
        netIncome: 0,
        foreignTaxSuffered: 0,
        foreignTaxSufferedSGD: 0,
      }
    );
  }, [foreignIncomeRows]);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          currency: company.currency,
          handleSave,
          deleteSchedule,
          showSettings: true,
          hasActiveSettings: false,
          onSettingsClick: () => setSettingsDialogOpen(true),
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          functionalCurrency={functionalCurrency}
          functionalCurrencyRate={exchangeRate}
          breakdownRows={breakdownRows}
          foreignIncomeRows={foreignIncomeRows}
          foreignIncomeTotals={foreignIncomeTotals}
          ftcCalculatedData={ftcCalculatedData}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />
      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={schedule.taxAdjustment}
        allowedToEditSchName
        allowedToAdjustTax={false}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <SectionBox
            title='Foreign Tax Credit Claim'
            onAdd={undefined}
            unsavedCount={
              foreignIncomeRows?.filter((row) => !row.isLocked).length
            }
            totalCounts={foreignIncomeRows?.length}
            showUnsavedCount={foreignIncomeRows?.length > 0}
            savedTerm='locked'
            canEdit={canEditSchedule}
          >
            <ForeignIncomeTable
              canEdit={canEditSchedule}
              rows={foreignIncomeRows}
              functionalCurrency={functionalCurrency}
              functionalCurrencyRate={exchangeRate}
              onChange={handleForeignIncomeChange}
              onRemove={handleRemoveForeignIncomeRow}
              onLockRow={handleLockForeignIncomeRow}
              onUnlockRow={handleUnlockForeignIncomeRow}
              totals={foreignIncomeTotals}
            />
          </SectionBox>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <FtcForm
        companyType={company.companyType}
        foreignIncomeData={foreignIncomeRows}
        breakdownRows={breakdownRows}
        onBreakdownChange={handleBreakdownChange}
        onRecordData={recordFtcCalculatedData}
        canEdit={canEditSchedule}
      />

      <Divider sx={{ marginY: 4 }} />

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />

      <FtcSettings
        company={company}
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        onSave={handleSaveSettings}
      />
    </Box>
  );
};

export default FtcSchedule;
